import { z } from 'zod';

import { NOTIFY_EVENTS } from '../constants.js';

import { emailSchema } from './auth.js';
import { isoDateTimeSchema } from './common.js';

const notifyEventSchema = z.enum(NOTIFY_EVENTS);

// ---------- SMTP ----------

/** GET /api/email/smtp — password is NEVER returned; only a flag. */
export const smtpSettingsResponseSchema = z.object({
  host: z.string(),
  port: z.number().int(),
  secure: z.boolean(),
  user: z.string(),
  fromAddress: z.string(),
  passwordIsSet: z.boolean(),
  updatedAt: isoDateTimeSchema,
});
export type SmtpSettingsResponseDto = z.infer<typeof smtpSettingsResponseSchema>;

/**
 * PUT /api/email/smtp — empty/whitespace host, user, fromAddress, and password keep
 * their existing values (so GET → edit → PUT round-trips don't wipe saved settings).
 */
export const updateSmtpSettingsSchema = z.object({
  host: z.string().max(253).optional(),
  port: z.coerce.number().int().min(1).max(65_535).optional(),
  secure: z.boolean().optional(),
  user: z.string().max(200).optional(),
  password: z.string().max(255).optional(),
  fromAddress: z
    .string()
    .max(200)
    .optional()
    .refine((v) => v === undefined || !v.trim() || emailSchema.safeParse(v).success, {
      message: 'Неверный формат email',
    }),
});
export type UpdateSmtpSettingsDto = z.infer<typeof updateSmtpSettingsSchema>;

// ---------- Triggers ----------

export const emailTriggerSchema = z.object({
  event: notifyEventSchema,
  enabled: z.boolean(),
  updatedAt: isoDateTimeSchema,
});
export type EmailTriggerDto = z.infer<typeof emailTriggerSchema>;

export const updateEmailTriggersSchema = z.object({
  triggers: z.array(
    z.object({ event: notifyEventSchema, enabled: z.boolean() }),
  ),
});
export type UpdateEmailTriggersDto = z.infer<typeof updateEmailTriggersSchema>;

// ---------- Templates ----------

export const emailTemplateSchema = z.object({
  event: notifyEventSchema,
  subject: z.string().max(255),
  body: z.string(),
  updatedAt: isoDateTimeSchema,
});
export type EmailTemplateDto = z.infer<typeof emailTemplateSchema>;

export const upsertEmailTemplateSchema = z.object({
  event: notifyEventSchema,
  subject: z.string().min(1, 'Введите тему письма').max(255),
  body: z.string().min(1, 'Введите текст письма'),
});
export type UpsertEmailTemplateDto = z.infer<typeof upsertEmailTemplateSchema>;

// ---------- Test send ----------

export const testEmailRequestSchema = z.object({
  to: z.string().email().max(254),
});
export type TestEmailRequestDto = z.infer<typeof testEmailRequestSchema>;
